/**
 * sonicCues.js — Reclaim Premium Suite · Signature sonic micro-cues.
 *
 * A tiny Web-Audio API utility that synthesises premium transition
 * sounds on the fly (no MP3s needed, near-zero footprint). Three cues:
 *
 *   - tap()        soft mallet tone (state click / chip select)
 *   - threshold()  warmer gold bell (entering a new section)
 *   - bloom()      ascending arpeggio (session start / save complete)
 *
 * All cues respect prefers-reduced-motion (interpreted as "less
 * sensory load") and gracefully no-op if the AudioContext can't be
 * created — they NEVER crash the app.
 *
 * Usage:
 *   import { sonicCues } from '../Premium/sonicCues';
 *   sonicCues.tap();
 */

let _ctx = null;
let _enabled = true;

function ctx() {
  if (_ctx) return _ctx;
  if (typeof window === "undefined") return null;
  try {
    _ctx = new (window.AudioContext || window.webkitAudioContext)();
    return _ctx;
  } catch {
    _enabled = false;
    return null;
  }
}

function reducedMotion() {
  try {
    return window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch { return false; }
}

function envelope(node, ac, peak, attack, release) {
  node.gain.setValueAtTime(0.0001, ac.currentTime);
  node.gain.exponentialRampToValueAtTime(peak, ac.currentTime + attack);
  node.gain.exponentialRampToValueAtTime(0.0001, ac.currentTime + attack + release);
}

function play(freq, dur, gain = 0.06, type = "sine", attack = 0.012) {
  if (!_enabled || reducedMotion()) return;
  const ac = ctx(); if (!ac) return;
  try { if (ac.state === "suspended") ac.resume(); } catch { /* noop */ }
  const osc = ac.createOscillator();
  const g = ac.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  osc.connect(g); g.connect(ac.destination);
  envelope(g, ac, gain, attack, dur);
  osc.start();
  osc.stop(ac.currentTime + attack + dur + 0.05);
}

export const sonicCues = {
  /** Subtle mallet tap — for chip / button / lozenge selections. */
  tap() { play(720, 0.12, 0.04, "sine", 0.008); },

  /** Warm gold bell — for entering a new section / step. */
  threshold() {
    play(540, 0.55, 0.05, "sine", 0.04);
    setTimeout(() => play(810, 0.45, 0.035, "sine", 0.06), 80);
  },

  /** Ascending arpeggio — for session start / save complete. */
  bloom() {
    [440, 550, 660, 880].forEach((f, i) => {
      setTimeout(() => play(f, 0.4, 0.045, "sine", 0.02), i * 90);
    });
  },

  /** Allow the user to disable globally (a Settings toggle could
   *  flip this). */
  setEnabled(v) { _enabled = !!v; },

  /** Probe — useful in tests. */
  isEnabled() { return _enabled && !reducedMotion(); },
};

export default sonicCues;
